import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Paper,
  Typography,
  AppBar,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditStockInward = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { stockInwardID } = useParams();
  const navigate = useNavigate();

  const [stockInward, setStockInward] = useState({
    vendorID: "",
    invoiceNumber: "",
    invoiceDate: "",
    receivedDate: "",
    invoiceCopy: null,
    invoiceCopyBase64: "",
  });

  const [items, setItems] = useState([]); // Array of item objects
  const [vendors, setVendors] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch vendors
  const fetchVendors = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Inventory/Vendor/GetVendors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({ vendorId: 0 }),
      });
      if (response.ok) {
        const data = await response.json();
        setVendors(data.vendors || []);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  // Fetch inventory items
  const fetchInventoryItems = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/Item/GetInventoryItems?itemId=0`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setInventoryItems(data);
      }
    } catch (error) {
      console.error("Error fetching inventory items:", error);
    }
  };

  // Fetch tax rates
  const fetchTaxRates = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Inventory/Item/GetTaxRates`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTaxes(data.taxRates || []);
      }
    } catch (error) {
      console.error("Error fetching tax rates:", error);
    }
  };

  // Fetch stock inward details
  const fetchStockInwardDetails = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/InwardItem/GetStockInwardForUpdate/${stockInwardID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      if (response.ok) {
        const responseData = await response.json();
        setStockInward({
          vendorID: responseData.vendorID || "",
          invoiceNumber: responseData.invoiceNumber || "",
          invoiceDate: responseData.invoiceDate || "",
          receivedDate: responseData.receivedDate || "",
          invoiceCopy: null,
          invoiceCopyBase64: "",
        });
        setItems(responseData.items || []);
      }
    } catch (error) {
      // alert("An error occurred while fetching stock inward details.");
      setSnackbarMessage('An error occurred while fetching stock inward details.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file)
    // window.alert("⚠️ Please upload an invoice copy.");
    {
      setSnackbarMessage('⚠️ Please upload an invoice copy.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }

    setStockInward((prev) => ({ ...prev, invoiceCopy: file }));
    const reader = new FileReader();
    reader.onload = () => {
      setStockInward((prev) => ({
        ...prev,
        invoiceCopyBase64: reader.result.split(",")[1],
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle item change
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = field === "quantityReceived" ? Number(value) : value;
    setItems(updatedItems);
  };

  // Update function remains unchanged
  const handleUpdate = async () => {
    try {
      const formData = new FormData();
      formData.append("vendorID", stockInward.vendorID);
      formData.append("invoiceNumber", stockInward.invoiceNumber);
      formData.append("invoiceDate", stockInward.invoiceDate);
      formData.append("receivedDate", stockInward.receivedDate);

      // Always send base64 string regardless of file upload
      if (stockInward.invoiceCopyBase64) {
        formData.append("invoiceCopyBase64", stockInward.invoiceCopyBase64);
      }

      if (stockInward.invoiceCopy instanceof File) {
        formData.append("invoiceCopy", stockInward.invoiceCopy);
      } else if (stockInward.invoiceCopyBase64) {
        const mimeType = "application/pdf";
        const byteString = atob(stockInward.invoiceCopyBase64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeType });
        const file = new File([blob], "InvoiceCopy.pdf", { type: mimeType });
        formData.append("invoiceCopy", file);
      } else {
        setSnackbarMessage('⚠️ Please upload an invoice copy.');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }

      items.forEach((item, index) => {
        formData.append(`Items[${index}][ItemID]`, item.itemID);
        formData.append(`Items[${index}][QuantityReceived]`, item.quantityReceived);
        formData.append(`Items[${index}][UnitCost]`, item.unitCost);
        formData.append(`Items[${index}][TaxId]`, item.taxId);
      });

      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/InwardItem/UpdateStockInward/${stockInwardID}`,
        {
          method: "PUT",
          headers: {
            Authorization: sessionStorage.getItem("token"),
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // window.alert(errorData.message || "Update failed.");
        setSnackbarMessage(errorData.message || "Update failed.");
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
        return;
      }

      // window.alert("Stock Inward updated successfully.");
      setSnackbarMessage("Stock Inward updated successfully.");
      setOpenSnackbar(true);
      setSnackbarSeverity("success");
      navigate(-1);
    } catch (error) {
      // window.alert("Failed to update");
      setSnackbarMessage("Failed to update");
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  // Load data
  useEffect(() => {
    fetchVendors();
    fetchInventoryItems();
    fetchTaxRates();
    fetchStockInwardDetails();
  }, []);

  return (
    <div className="container mt-4">
      <LoadingBar progress={loadingBarProgress} color="#986ac6" height={5} />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h6">Edit Stock Inward</Typography>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, marginTop: 16 }}>
        {isLoading ? (
          <Typography>Loading stock inward details...</Typography>
        ) : (
          <>
            {/* Vendor Dropdown */}
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={stockInward.vendorID}
                onChange={(e) =>
                  setStockInward({ ...stockInward, vendorID: e.target.value })
                }
                label="Vendor"
              >
                {vendors.map((v) => (
                  <MenuItem key={v.vendorID} value={v.vendorID}>
                    {v.vendorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Invoice Number"
              variant="outlined"
              fullWidth
              value={stockInward.invoiceNumber}
              onChange={(e) =>
                setStockInward({ ...stockInward, invoiceNumber: e.target.value })
              }
              sx={{ marginBottom: 2 }}
            />

            <TextField
              label="Invoice Date"
              type="date"
              variant="outlined"
              fullWidth
              value={stockInward.invoiceDate}
              onChange={(e) =>
                setStockInward({ ...stockInward, invoiceDate: e.target.value })
              }
              sx={{ marginBottom: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Received Date"
              type="date"
              variant="outlined"
              fullWidth
              value={stockInward.receivedDate}
              onChange={(e) =>
                setStockInward({ ...stockInward, receivedDate: e.target.value })
              }
              sx={{ marginBottom: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Upload Invoice Copy
            </Typography>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={handleFileUpload}
              style={{ marginBottom: 16 }}
            />

            {/* Items Section */}
            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Items
            </Typography>
            {items.map((item, index) => {
              const itemName =
                inventoryItems.find((i) => i.itemId === item.itemID)?.itemName ||
                "Unknown Item";
              const taxName =
                taxes.find((t) => t.taxId === item.taxId)?.taxName || "No Tax";

              return (
                <div key={index} style={{ marginBottom: 16 }}>
                  {/* Item Dropdown */}
                  <FormControl fullWidth sx={{ marginBottom: 1 }}>
                    <InputLabel>Item</InputLabel>
                    <Select
                      value={item.itemID}
                      onChange={(e) =>
                        handleItemChange(index, "itemID", e.target.value)
                      }
                      label="Item"
                    >
                      {inventoryItems.map((i) => (
                        <MenuItem key={i.itemId} value={i.itemId}>
                          {i.itemName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Quantity Received"
                    variant="outlined"
                    fullWidth
                    value={item.quantityReceived}
                    onChange={(e) =>
                      handleItemChange(index, "quantityReceived", e.target.value)
                    }
                    sx={{ marginBottom: 1 }}
                  />

                  <TextField
                    label="Unit Cost"
                    variant="outlined"
                    fullWidth
                    value={item.unitCost}
                    onChange={(e) =>
                      handleItemChange(index, "unitCost", e.target.value)
                    }
                    sx={{ marginBottom: 1 }}
                  />

                  {/* Tax Dropdown */}
                  <FormControl fullWidth sx={{ marginBottom: 1 }}>
                    <InputLabel>Tax</InputLabel>
                    <Select
                      value={item.taxId || ""}
                      onChange={(e) =>
                        handleItemChange(index, "taxId", e.target.value)
                      }
                      label="Tax"
                    >
                      {taxes.map((t) => (
                        <MenuItem key={t.taxId} value={t.taxId}>
                          {t.tax}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              );
            })}

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdate}
              sx={{ marginRight: 2 }}
            >
              Update
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </>
        )}
      </Paper>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </div>
  );
};

export default EditStockInward;